// How the activity log reads: each action's label, icon and colour, and the
// formatting of its entries. The backend words each entry (utils/activity).

export type ActivityTone = 'green' | 'indigo' | 'amber' | 'red' | 'slate' | 'violet' | 'sky';

export interface ActivityActionInfo {
  value: string;
  label: string;
  icon: string;
  tone: ActivityTone;
}

export interface ActivityActionGroup {
  label: string;
  actions: ActivityActionInfo[];
}

/** Every action, grouped the way the filter lists them. */
export const ACTIVITY_ACTION_GROUPS: ActivityActionGroup[] = [
  {
    label: 'Account',
    actions: [
      { value: 'login', label: 'Login', icon: 'pi-sign-in', tone: 'green' },
      { value: 'login_failed', label: 'Login Failed', icon: 'pi-exclamation-triangle', tone: 'red' },
      { value: 'logout', label: 'Logout', icon: 'pi-sign-out', tone: 'slate' },
      { value: 'signup', label: 'Sign Up', icon: 'pi-user-plus', tone: 'violet' },
      { value: 'password_reset', label: 'Password Reset', icon: 'pi-key', tone: 'amber' },
      { value: 'invite', label: 'Invite', icon: 'pi-send', tone: 'sky' },
    ],
  },
  {
    label: 'Changes',
    actions: [
      { value: 'create', label: 'Create', icon: 'pi-plus', tone: 'green' },
      { value: 'update', label: 'Update', icon: 'pi-pencil', tone: 'indigo' },
      { value: 'delete', label: 'Delete', icon: 'pi-trash', tone: 'red' },
      { value: 'activate', label: 'Activate', icon: 'pi-check-circle', tone: 'green' },
      { value: 'deactivate', label: 'Deactivate', icon: 'pi-ban', tone: 'amber' },
      { value: 'archive', label: 'Archive', icon: 'pi-inbox', tone: 'slate' },
      { value: 'unarchive', label: 'Unarchive', icon: 'pi-replay', tone: 'sky' },
      { value: 'approve', label: 'Approve', icon: 'pi-thumbs-up', tone: 'green' },
      { value: 'reject', label: 'Reject', icon: 'pi-times-circle', tone: 'red' },
    ],
  },
  {
    label: 'Data',
    actions: [
      { value: 'import', label: 'Import', icon: 'pi-upload', tone: 'violet' },
      { value: 'export', label: 'Export', icon: 'pi-download', tone: 'violet' },
    ],
  },
];

const ACTIONS: Record<string, ActivityActionInfo> = ACTIVITY_ACTION_GROUPS
  .flatMap((group) => group.actions)
  .reduce((all, action) => ({ ...all, [action.value]: action }), {});

/** An action's label, icon and colour — a plain fallback for one this app doesn't know yet. */
export function activityAction(value: string): ActivityActionInfo {
  return ACTIONS[value] || {
    value,
    label: value.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()),
    icon: 'pi-circle',
    tone: 'slate',
  };
}

/** A description with its quoted names picked out, to show them in bold. */
export function descriptionParts(description: string): Array<{ text: string; name: boolean }> {
  return String(description || '')
    .split(/(“[^”]*”)/)
    .filter(Boolean)
    .map((text) => ({ text, name: /^“[^”]*”$/.test(text) }));
}

const dateFormatter = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
const timeFormatter = new Intl.DateTimeFormat('en-US', { hour: 'numeric', minute: '2-digit', second: '2-digit' });
const fullFormatter = new Intl.DateTimeFormat('en-US', {
  weekday: 'long',
  year: 'numeric',
  month: 'long',
  day: 'numeric',
  hour: 'numeric',
  minute: '2-digit',
  second: '2-digit',
  timeZoneName: 'short',
});

/** "Sep 28, 2026" and "4:12:05 PM", in the viewer's time zone, plus the full form for a tooltip. */
export function exactTime(value: string | number | Date): { date: string; time: string; full: string; iso: string } {
  const at = new Date(value);
  if (Number.isNaN(at.getTime())) return { date: '', time: '', full: '', iso: '' };
  return {
    date: dateFormatter.format(at),
    time: timeFormatter.format(at),
    full: fullFormatter.format(at),
    iso: at.toISOString(),
  };
}

const ISO_DATE = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/;
const dateTimeValue = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });
const dateValue = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

const ROLE_VALUES: Record<string, string> = {
  'super-admin': 'Super Admin',
  owner: 'Owner',
  admin: 'Admin',
  user: 'User',
  mechanic: 'Mechanic',
};

/** One side of a changed field, as a person reads it. */
export function changeValue(field: string, value: unknown): string {
  if (value === null || value === undefined || value === '') return 'None';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (field === 'role' && typeof value === 'string') return ROLE_VALUES[value] || value;
  if (typeof value === 'string' && ISO_DATE.test(value)) {
    const at = new Date(value);
    if (!Number.isNaN(at.getTime())) {
      // Midnight UTC is a date the form picked, not a moment.
      const dateOnly = at.getUTCHours() === 0 && at.getUTCMinutes() === 0 && at.getUTCSeconds() === 0;
      return dateOnly ? dateValue.format(at) : dateTimeValue.format(at);
    }
  }
  return String(value);
}

/** "Sep 1 – Sep 30, 2026" for an export's date range. */
export function dateRange(from?: string | null, to?: string | null): string {
  const format = (value: string) => dateValue.format(new Date(value.length === 10 ? `${value}T00:00:00` : value));
  if (from && to) return `${format(from)} – ${format(to)}`;
  if (from) return `from ${format(from)}`;
  if (to) return `through ${format(to)}`;
  return '';
}

/**
 * "Chrome on Windows" from a browser's user agent; null for an API client or
 * script, which has no browser to name.
 */
export function deviceOf(userAgent?: string | null): { label: string; mobile: boolean } | null {
  if (!userAgent) return null;
  const ua = userAgent;
  const browser = /Edg(e|A|iOS)?\//.test(ua) ? 'Edge'
    : /OPR\/|Opera/.test(ua) ? 'Opera'
    : /SamsungBrowser\//.test(ua) ? 'Samsung Internet'
    : /Chrome\/|CriOS\//.test(ua) ? 'Chrome'
    : /Firefox\/|FxiOS\//.test(ua) ? 'Firefox'
    : /Safari\//.test(ua) ? 'Safari'
    : null;
  const os = /Windows/.test(ua) ? 'Windows'
    : /iPhone|iPad|iPod/.test(ua) ? 'iOS'
    : /Android/.test(ua) ? 'Android'
    : /CrOS/.test(ua) ? 'ChromeOS'
    : /Mac OS X|Macintosh/.test(ua) ? 'macOS'
    : /Linux/.test(ua) ? 'Linux'
    : null;
  if (!browser && !os) return null;
  return { label: [browser, os].filter(Boolean).join(' on '), mobile: /Mobi|iPhone|iPod|Android/.test(ua) };
}

/**
 * The support session an entry was made in — Screenkode staff inside the
 * organization's app, launched from the admin — if it was made in one.
 */
export function activitySupportSession(details?: Record<string, any> | null): { id: string; app: string; title: string } | null {
  const session = details && details.supportSession;
  if (!session || typeof session !== 'object') return null;
  const app = ACTIVITY_SOURCES.find((source) => source.value === session.app);
  return {
    id: String(session.id || ''),
    app: String(session.app || ''),
    title: `Done by Screenkode staff inside ${app ? app.label : 'the app'}, through a support session launched from the admin`,
  };
}

/** The apps the log tells apart, in the order the filter lists them. */
export const ACTIVITY_SOURCES = [
  { value: 'chabad_digital', label: 'Chabad Digital' },
  { value: 'screenkode', label: 'Screenkode' },
  { value: 'local', label: 'Local' },
];

export interface ActivitySourceInfo {
  /** chabad_digital, screenkode, local — or other, for an address that is neither. */
  value: string;
  /** "Chabad Digital", "Screenkode Admin", "Local"… */
  label: string;
  /** Which brand's mark to show, if any. */
  brand: 'chabad_digital' | 'screenkode' | null;
  /** Where exactly: the app, the admin or the API, and its address. */
  title: string;
}

/**
 * The tag an entry carries for where it was done: which brand's app — its
 * admin or its API, when that was it — told from the address it came from.
 */
export function activitySource(source?: string | null, host?: string | null): ActivitySourceInfo | null {
  if (!source && !host) return null;
  const where = host || '';
  const name = where.replace(/:\d+$/, '');
  if (source === 'chabad_digital' || source === 'screenkode') {
    const brand = source === 'chabad_digital' ? 'Chabad Digital' : 'Screenkode';
    const admin = /^admin\./.test(name);
    const api = /^(dev\.)?api\./.test(name);
    let title = `Done in the ${brand} app`;
    if (admin) title = `Done in the ${brand} admin`;
    if (api) title = `Sent straight to the ${brand} API`;
    return {
      value: source,
      label: admin ? `${brand} Admin` : brand,
      brand: source,
      title: where ? `${title} · ${where}` : title,
    };
  }
  if (source === 'local') {
    return { value: 'local', label: 'Local', brand: null, title: `Local development${where ? ` · ${where}` : ''}` };
  }
  return { value: 'other', label: name, brand: null, title: `From ${where}` };
}
